import { ref } from "vue";
import Swal from "sweetalert2";
import i18n from "@/i18n/index";
const Toast = Swal.mixin({
  toast: true,
  position: "top-right",
  showConfirmButton: false,
  timer: 12000,
  timerProgressBar: true,
});

import { useAuthenticatedFetch } from "../auth/useAuthenticatedFetch";
import router from "@/router/index";
import { users, clubUUID, userUUID, user } from "@/shared/state";
import {
  User,
  UserResponse,
  UserRequest,
  ResetPasswordRequest,
} from "@/interfaces/user";
import { useAppLock } from "@/composables/lock/useAppLock";

const { authenticatedFetch } = useAuthenticatedFetch();
const API_URL = process.env.VUE_APP_ROOT_API;
export function useUser() {
  const { setPin } = useAppLock();
  const error = ref<string | null>(null);
  const isRequestUser = ref<boolean | false>(false);
  const getUserAssistances = async () => {
    try {
      isRequestUser.value = true;
      const response = await authenticatedFetch(
        `${API_URL}user/assistances/${userUUID.value}`,
        {
          method: "GET",
        },
        false,
        "",
        false,
      );

      if (response?.data) {
        users.data = response?.data || [];
        isRequestUser.value = false;
        return true;
      } else {
        isRequestUser.value = false;
        return false;
      }
    } catch (err: any) {
      error.value = err.message;
      isRequestUser.value = false;
    }
  };

  function hasAdminRole() {
    // Parse the roles field from JSON string to array
    const rolesArray = JSON.parse(user?.value?.roles || "[]");
    // Check if 'admin' is in the array
    return rolesArray.includes("admin");
  }

  function hasSuperAdminRole() {
    // Parse the roles field from JSON string to array
    const rolesArray = JSON.parse(user?.value?.roles || "[]");
    // Check if 'admin' is in the array
    return rolesArray.includes("super-admin");
  }
  function hasAssistanceRole() {
    // Parse the roles field from JSON string to array
    const rolesArray = JSON.parse(user?.value?.roles || "[]");
    // Check if 'admin' is in the array
    return rolesArray.includes("assistance");
  }

  const changeStatus = async (body) => {
    try {
      isRequestUser.value = true;
      const response = await authenticatedFetch(
        `${API_URL}user/affected/club`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
        true,
        "",
        true,
      );
      if (response) {
        const clubUserEdited = response?.data;
        const index = users?.data?.findIndex(
          (user) => user?.id === clubUserEdited?.user_id,
        );
        if (index !== -1) {
          const indexClubUserEdited = users.data[index]?.clubs_user?.findIndex(
            (user) => user?.id === clubUserEdited?.id,
          );
          if (indexClubUserEdited !== -1) {
            users.data[index].clubs_user[indexClubUserEdited].is_active =
              clubUserEdited.is_active;
          }
        }
      }
      isRequestUser.value = false;
    } catch (err: any) {
      error.value = err.message;
      isRequestUser.value = false;
    }
  };

  const addUser = async (body: UserRequest) => {
    try {
      isRequestUser.value = true;
      const response = await authenticatedFetch(
        `${API_URL}users/store`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        "",
        true,
      );
      if (response?.data) {
        const userAdded: User = response?.data;
        users?.data?.unshift(userAdded);
        isRequestUser.value = false; 
        return true;
      } else {
        isRequestUser.value = false;
        return false;
      }
    } catch (err: any) {
      error.value = err.message;
      isRequestUser.value = false;
    }
  };

  const editUser = async (body: UserRequest) => {
    try {
      isRequestUser.value = true;
      const response = await authenticatedFetch(
        `${API_URL}users/edit/${body?.id}`,
        {
          method: "PUT",
          body: JSON.stringify(body),
        },
        true,
        "",
        true,
      );
      if (response?.data) {
        const userEdited: User = response?.data;
        const index = users?.data?.findIndex(
          (user) => user?.id === userEdited?.id,
        );
        if (index !== -1) {
          users.data[index] = userEdited;
        }
        isRequestUser.value = false; 
        return true;
      } else {
        isRequestUser.value = false;
        return false;
      }
    } catch (err: any) {
      error.value = err.message;
      isRequestUser.value = false;
    }
  };

  const deleteUser = async (id: number) => {
    try {
      isRequestUser.value = true;
      const response = await authenticatedFetch(
        `${API_URL}users/delete/${id}`,
        {
          method: "DELETE",
        },
        true,
        "",
        true,
      );
      const index = users?.data?.findIndex((user) => user?.id === id);
      if (index !== -1) {
        users.data.splice(index, 1);
      }
      isRequestUser.value = false;
    } catch (err: any) {
      error.value = err.message;
      isRequestUser.value = false;
    }
  };

  const resetPassword = async (body: ResetPasswordRequest) => {
    try {
      isRequestUser.value = true;
      const response = await authenticatedFetch(
        `${API_URL}password/profile-reset`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        true,
        "",
        true,
      );

      if (response?.errors) {
        // Toast.fire({
        //     title: "error",
        //     icon: 'error',
        // });
        isRequestUser.value = false;
        return false;
      } else {
        Toast.fire({
          title: i18n.t("message.toast.add.title"),
          icon: "success",
        });
        isRequestUser.value = false;
        return true;
      }
    } catch (err: any) {
      error.value = err.message;
      isRequestUser.value = false;
    }
  };

  return {
    resetPassword,
    deleteUser,
    addUser,
    editUser,
    changeStatus,
    hasAdminRole,
    hasAssistanceRole,
    hasSuperAdminRole,
    getUserAssistances,
    isRequestUser,
  };
}
